import { useAtom } from 'jotai';
import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { OTCPageAtom, OTCPageSizeAtom } from './OTCAtoms';
import { useNavigate } from 'react-router-dom';
import { useLoader } from '../../_helper/LoaderContext';
import { usePharmacy } from '../../_helper/PharmacyProvider';
import { fetchAllPharmacyItems, getItemsList } from '../PharmacyMedicineAdd/PharmacyMedicineService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Breadcrumbs, ToolTip } from '../../AbstractElements';
import { Card, CardHeader, Col, Input } from 'reactstrap';
import { Filter, PlusCircle, X } from 'react-feather';
import { FaPlusCircle, FaUpload } from 'react-icons/fa';
import { FormLabel, Table } from 'react-bootstrap';
import MyPagination from '../pagination';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useCurrency } from '../../_helper/CurrencyContext';

const OTCProducts = () => {
	const { t } = useTranslation();
	const { getCurrencySymbol } = useCurrency();
	const [searchValue, setSearchValue] = useState('');
	const [totalItems, setTotalItems] = useState(0);
	const [page, setPage] = useAtom(OTCPageAtom);
	const [size, setSize] = useAtom(OTCPageSizeAtom);
	// const [page, setPage] = useState(1);
	// const [size, setSize] = useState(10);
	const [isClearing, setIsClearing] = useState(false);
	const history = useNavigate();
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const { pharmacyPOS } = usePharmacy();

	const [editTooltipOpen, setEditTooltipOpen] = useState(false);
	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
	const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
	const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);
	const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
	const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);

	const [brandTooltipOpen, setBrandTooltipOpen] = useState(false);
	const [saltTooltipOpen, setSaltTooltipOpen] = useState(false);
	const [manuTooltipOpen, setManuTooltipOpen] = useState(false);
	const toggleBrandTooltip = () => setBrandTooltipOpen(!brandTooltipOpen);
	const toggleSaltTooltip = () => setSaltTooltipOpen(!saltTooltipOpen);
	const toggleManuTooltip = () => setManuTooltipOpen(!manuTooltipOpen);

	const [formSubmitted, setFormSubmitted] = useState(false);

	const [medicines, setMedicines] = useState([])

	useEffect(() => {
		getMedicineList();
	}, [page, size]);

	const getMedicineList = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			page: page,
			limit: size,
			search: searchValue.trim(),
			isOTC: true
		};
		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getItemsList(body);
			// const dec_data = decryptResponseData(response);
			// if (dec_data) {
			setMedicines(response.data.data.items || []);
			const totalCount = response.data.data.total || 0;
			setTotalItems(Math.ceil(totalCount / size));
			// }
		} catch (error) {
			console.error("Error fetching sales history:", error);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const [showImportModal, setShowImportModal] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		setSelectedFile(file);
	};

	const handleUploadOld = () => {
		// API integration pending
		console.log("Uploading file:", selectedFile);
		setShowImportModal(false);
	};

	const handleSearchButtonClick = () => {
		if (searchValue.trim !== "") {
			setPage(1); // Reset page to 1
			getMedicineList(); // Trigger the API call for search
		}
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue(''); // Clear the search value
	};

	useEffect(() => {
		if (isClearing) {
			getMedicineList(); // Trigger the API call when clearing search
			setIsClearing(false); // Reset clearing state
		}
	}, [isClearing]);

	// Handle search field input change (but don't trigger API call)
	const handleSearchChange = (event) => {
		setSearchValue(event.target.value); // Just update the state
	};

	const handlePageChange = (pageNumber) => {
		setPage(pageNumber);
	};

	const handlePageSizeChange = (e) => {
		const newSize = parseFloat(e.target.value); // Parse the value without triggering a state update
		setSize(newSize); // Update the state after parsing the value
		setPage(1); // Reset currentPage to 1 when changing page size
	}

	// Event handler for opening add component
	const addMedicine = () => {
		history(`${process.env.PUBLIC_URL}/add-OTC`);
	};

	// Event handler for opening edit component
	const handleEditClick = (getID) => {
		history(`${process.env.PUBLIC_URL}/update-OTC/${getID}`);
	};

	// Event handler for opening view component
	const handleViewClick = (getID) => {
		history(`${process.env.PUBLIC_URL}/view-OTC/${getID}`);
	};

	const downloadCSV = () => {
		const link = document.createElement('a');
		link.href = '/sample_medicines.xlsx'; // path to your CSV in assets folder
		link.download = 'medicines.csv'; // name for downloaded file
		link.click();
	}

	const exportToExcel = async () => {
		try {
			setLoading(true);
			const allItems = await fetchAllPharmacyItems({
				clinicId: pharmacyPOS?.hospital_id,
				search: searchValue.trim(),
				isOTC: true,
			});

			if (!allItems.length) {
				toast.warning(t("No items to export"));
				return;
			}

			const rows = allItems.map((item) => ({
				"Product Name": item.brandName,
				"Manufacturer Name": item.manufacturer,
				"Product Form": item.productForm,
				"Package": item.package,
				"Quantity": item.packageQuantity,
				"Price": `${getCurrencySymbol()}${item.regularPrice}`,
			}));

			const worksheet = XLSX.utils.json_to_sheet(rows);
			const colWidths = Object.keys(rows[0] || {}).map((key) => ({
				wch: Math.max(
					key.length,
					...rows.map((row) => String(row[key] || "").length)
				),
			}));
			worksheet["!cols"] = colWidths;

			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, "OTC Products");

			const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
			const data = new Blob([excelBuffer], { type: "application/octet-stream" });
			saveAs(data, "otc_products.xlsx");
		} catch (error) {
			console.error("Excel export failed:", error);
			toast.warning(t("Failed to export items."));
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		const confirm = await Swal.fire({
			title: t("Are you sure?"),
			text: t("You want to delete this item"),
			icon: "warning",
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn-primary',
				cancelButton: 'btn-danger',
			},
			confirmButtonText: t("Yes, Delete It!"),
		});

		if (confirm.isConfirmed) {
			// try {
			// 	setLoading(true);
			// 	const response = await deleteItem(id); // your delete API
			// 	if (response?.data?.status === "success") {
			// 		toast.success(response.data.message);
			// 		getMedicineList(); // refresh the table
			// 	}
			// 	setLoading(false);
			// } catch (err) {
			// 	setLoading(false);
			// 	console.error("Delete error:", err);
			// }
		}
	};
	return (
		<Fragment>
			<Breadcrumbs mainTitle={t('OTC')} parent={t('Dashboard')} title={t('OTC')} />
			<Col sm="12">
				<Card>
					<CardHeader className="d-flex justify-content-end align-items-center">
						{/* <H5>{t('Medicines')}</H5> */}
						<div className="d-flex align-items-center">
							<button
								className="btn btn-primary d-flex align-items-center me-2"
								style={{ color: 'white', height: "40px" }}
								to=""
								onClick={() => addMedicine()}>
								<FaPlusCircle className="me-2" /> {t("Add OTC Item")}
							</button>
							{/* <button
								className="btn btn-primary d-flex align-items-center me-2"
								style={{ color: 'white', height: "40px" }}
								to=""
								onClick={() => setShowImportModal(true)}>
								<FaUpload className="me-2" /> {t("Import Medicines")}
							</button> */}
							{/* <button
								className="btn btn-primary d-flex align-items-center"
								style={{ color: 'white', height: "40px" }}
								to=""
								onClick={exportToExcel}>
								<FaUpload className="me-2" /> {t("Export to Excel")}
							</button> */}
						</div>
					</CardHeader>
					<div className="row" style={{ alignItems: "center", padding: "12px 24px" }}>
						<Col sm="8" lg="8" xl="10" md="8">
							<Input
								name="SearchData"
								id="SearchData"
								type="text"
								placeholder={t("Search by product name and product form")}
								value={searchValue}
								onChange={handleSearchChange}
								style={{
									lineHeight: 1.8, fontSize: "1rem !important"
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										handleSearchButtonClick();
									}
								}}
							/>
						</Col>
						<Col className="col-xl-2 col-lg-4 col-md-4 col-sm-4 d-flex justify-content-start align-items-center" style={{ gap: '18px' }}>
							<button id='FilterToolTip' className='btn btn-primary' style={{ height: "40px" }} onClick={handleSearchButtonClick}>
								<Filter />
							</button>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: filterTooltipOpen,
									target: 'FilterToolTip',
									toggle: toggleFilterTooltip,
								}} >
								<strong>{t("Filter")}</strong>
							</ToolTip>
							<button id='ClearToolTip' className='btn btn-secondary' style={{ height: "40px" }} onClick={handleClear}>
								<X />
							</button>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: clearTooltipOpen,
									target: 'ClearToolTip',
									toggle: toggleClearTooltip,
								}} >
								<strong>{t("Clear")}</strong>
							</ToolTip>
						</Col>
					</div>
					<div className="card-block">
						<div className='row' style={{ padding: "12px 24px" }}>
							<Col sm="12" lg="12" xl="12">
								<div className="table-responsive">
									<Table className='mb-0 table-center table-bordered table-hover'>
										<thead>
											<tr>
												<th>{t('Product Name')}</th>
												{/* <th>{t('Salt Name / Composition')}</th> */}
												<th>{t('Manufacturer Name')}</th>
												<th>{t('Product Form')}</th>
												<th>{t("Package")}</th>
												<th>{t('Quantity')}</th>
												<th>{t('Price')}</th>
												<th className="text-center">{t("Action")}</th>
											</tr>
										</thead>
										<tbody>
											{medicines.map((med, index) => (
												<tr key={index}>
													{/* Brand */}
													<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
														<span id={`brandToolTip-${index}`}>{med.brandName}</span>
														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: brandTooltipOpen === index,   // track open by index
																target: `brandToolTip-${index}`,
																toggle: () =>
																	setBrandTooltipOpen(brandTooltipOpen === index ? null : index),
															}}
														>
															<strong>{med.brandName}</strong>
														</ToolTip>
													</td>

													{/* Salt */}
													{/* <td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
														<span id={`saltToolTip-${index}`}>{med.saltName}</span>
														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: saltTooltipOpen === index,
																target: `saltToolTip-${index}`,
																toggle: () =>
																	setSaltTooltipOpen(saltTooltipOpen === index ? null : index),
															}}
														>
															<strong>{med.saltName}</strong>
														</ToolTip>
													</td> */}

													{/* Manufacturer */}
													<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
														<span id={`manuToolTip-${index}`}>{med.manufacturer}</span>
														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: manuTooltipOpen === index,
																target: `manuToolTip-${index}`,
																toggle: () =>
																	setManuTooltipOpen(manuTooltipOpen === index ? null : index),
															}}
														>
															<strong>{med.manufacturer}</strong>
														</ToolTip>
													</td>
													<td>{med.productForm}</td>
													<td>{med.package}</td>
													<td>{med.packageQuantity}</td>
													<td>{med.currency} {med.regularPrice}</td>
													<td className='text-center'>
														<button className='success-action-button me-1' id="ViewToolTip" onClick={() => handleViewClick(med?._id)}>
															<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
															<ToolTip
																attrToolTip={{
																	placement: 'top',
																	isOpen: viewTooltipOpen,
																	target: 'ViewToolTip',
																	toggle: toggleViewTooltip,
																}} >
																<strong>{t("View")}</strong>
															</ToolTip>
														</button>
														<button className='success-action-button me-1' id="EditToolTip" onClick={() => handleEditClick(med?._id)}>
															<i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
															<ToolTip
																attrToolTip={{
																	placement: 'top',
																	isOpen: editTooltipOpen,
																	target: 'EditToolTip',
																	toggle: toggleEditTooltip,
																}} >
																<strong>{t("Edit")}</strong>
															</ToolTip>
														</button>
														{/* <button className='danger-action-button' id="DeleteToolTip" onClick={() => handleDelete(med?._id)}>
															<i className="fa fa-trash" style={{ fontSize: "18px" }}></i>
															<ToolTip
																attrToolTip={{
																	placement: 'top',
																	isOpen: deleteTooltipOpen,
																	target: 'DeleteToolTip',
																	toggle: toggleDeleteTooltip,
																}} >
																<strong>{t("Delete")}</strong>
															</ToolTip>
														</button> */}
													</td>
												</tr>
											))}
											{medicines.length === 0 && (
												<tr>
													<td colSpan="7" className="text-center text-muted">
														{t('No OTC products found')}
													</td>
												</tr>
											)}
										</tbody>
									</Table>
								</div>
							</Col>
							<Col sm="12" lg="12" xl="12" className="mt-4">
								<div className="text-center d-md-flex align-items-center justify-content-between">
									<div className="pt-3 show-entries">
										<FormLabel>{t("Show")}&nbsp;</FormLabel>
										<select style={{ display: 'unset', width: 'auto', padding: '0.375rem 2.25rem 0.375rem 0.75rem' }} value={size} onChange={handlePageSizeChange} className="form-control">
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={20}>20</option>
											<option value={30}>30</option>
											<option value={50}>50</option>
										</select>&nbsp;
										<FormLabel>{t("entries")}</FormLabel>
									</div>
									<MyPagination totalPages={totalItems} currentPage={page} onPageChange={handlePageChange} />
								</div>
							</Col>
						</div>
					</div>
				</Card>
			</Col>

		</Fragment>
	);
}

export default OTCProducts